import { useMemo, useState } from 'react';
import { X, Printer, Download, Loader2 } from 'lucide-react';
import { formatMoney } from '@/utils/currency';
import { downloadQuotationPdf, printQuotation } from '@/utils/quotationExportPdf';
import toast from 'react-hot-toast';

interface ProductItem {
  item_code: string;
  item_name: string;
  line_comment?: string;
  quantity: number;
  available_stock: number;
  rate: number;
  amount: number;
  cost_price?: number;
}

interface RecordDetail {
  id: string;
  name: string;
  tab: string;
  status: string;
  creation: string;
  invoice_date?: string;
  expected_date?: string;
  valid_till?: string;
  delivery_date?: string;
  payment_method?: string;
  owner?: string;
  items: ProductItem[];
  financials: {
    subtotal: number;
    vat: number;
    discount?: number;
    discount_amount?: number;
    discount_type?: string;
    discount_value?: number;
    discount_percent?: number;
    total: number;
    paid: number;
    balance: number;
    profit?: number;
    profit_margin?: number;
  };
  customer?: {
    name?: string;
    customer_name?: string;
    mobile?: string;
    email?: string;
    address?: string;
    city?: string;
    province?: string;
    postal_code?: string;
  };
  notes?: string;
  discount_amount?: number;
  discount_type?: string;
  discount_value?: number;
  discount_percent?: number;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  quotation: RecordDetail | null;
  profile?: any;
}

const ROWS_FIRST_PAGE = 14;
const ROWS_NEXT_PAGE = 22;

function chunkItems<T>(items: T[]): T[][] {
  if (!items.length) return [[]];
  const first = items.slice(0, ROWS_FIRST_PAGE);
  const rest = items.slice(ROWS_FIRST_PAGE);
  const pages: T[][] = [first];
  for (let i = 0; i < rest.length; i += ROWS_NEXT_PAGE) {
    pages.push(rest.slice(i, i + ROWS_NEXT_PAGE));
  }
  return pages;
}

export default function QuotationPdfModal({ isOpen, onClose, quotation, profile }: Props) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  const pages = useMemo(() => chunkItems(quotation?.items || []), [quotation?.items]);
  const pageOffsets = useMemo(() => {
    const offsets: number[] = [];
    let n = 0;
    for (const page of pages) {
      offsets.push(n);
      n += page.length;
    }
    return offsets;
  }, [pages]);

  if (!isOpen || !quotation) return null;

  const companyName = profile?.company_name || profile?.company?.name || profile?.company || 'Sale Desk';
  const rawAddress = profile?.address || '';
  const companyAddress =
    rawAddress.includes('frappe.') || rawAddress.includes('import ') ? '' : rawAddress;
  const companyPhone = profile?.phone || profile?.mobile || profile?.contact_no || '';
  const companyLogo = profile?.company_logo;

  const customerName = quotation.customer?.customer_name || quotation.customer?.name || 'Customer';
  const customerContact = [
    quotation.customer?.mobile,
    quotation.customer?.email,
    quotation.customer?.address,
    quotation.customer?.city,
    quotation.customer?.postal_code,
  ]
    .filter(Boolean)
    .join(', ');

  const quotationDate =
    quotation.invoice_date || new Date(quotation.creation || Date.now()).toLocaleDateString();
  const validUntil = quotation.valid_till || quotation.expected_date || '—';
  const preparer = quotation.owner || profile?.full_name || 'Staff';
  const footerText =
    typeof window !== 'undefined' && window.location.hostname.includes('kashly')
      ? 'Powered by Kashly'
      : 'Powered by Saledesk';

  const discAmt =
    Number(
      quotation.discount_amount ??
        quotation.financials?.discount_amount ??
        quotation.financials?.discount ??
        0,
    ) ||
    ((quotation.financials?.subtotal || 0) + (quotation.financials?.vat || 0) >
    (quotation.financials?.total || 0)
      ? Math.round(
          ((quotation.financials?.subtotal || 0) +
            (quotation.financials?.vat || 0) -
            (quotation.financials?.total || 0)) *
            100,
        ) / 100
      : 0);

  let discPct = quotation.financials?.discount_percent ?? quotation.discount_percent;
  if (discPct == null && quotation.notes) {
    const match =
      quotation.notes.match(/Discount(?:\s*Rate)?:\s*([\d.]+)%/i) ||
      quotation.notes.match(/Discount\s*\((?:%|percentage)\):\s*([\d.]+)/i);
    if (match) discPct = parseFloat(match[1]);
  }

  const formattedQuotationForDownload = {
    name: quotation.name,
    customer_name: customerName,
    customer: quotation.customer?.name,
    contact_display: customerContact,
    creation: quotation.creation,
    valid_till: quotation.valid_till || quotation.expected_date,
    owner: preparer,
    net_total: quotation.financials.subtotal,
    total_taxes_and_charges: quotation.financials.vat,
    discount_amount: quotation.discount_amount || quotation.financials?.discount_amount || 0,
    discount_percent: quotation.discount_percent || quotation.financials?.discount_percent,
    notes: quotation.notes,
    grand_total: quotation.financials.total,
    items: quotation.items.map((it) => ({
      item_name: it.item_name,
      item_code: it.item_code,
      line_comment: it.line_comment || '',
      quantity: it.quantity,
      rate: it.rate,
      amount: it.amount,
    })),
  };

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      await downloadQuotationPdf(formattedQuotationForDownload, profile);
      toast.success('Quotation PDF downloaded successfully');
    } catch (err) {
      toast.error('Failed to generate PDF');
      console.error(err);
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePrint = async () => {
    try {
      setIsPrinting(true);
      await printQuotation(formattedQuotationForDownload, profile);
    } catch (err) {
      toast.error('Failed to open PDF');
      console.error(err);
    } finally {
      setIsPrinting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-2 sm:p-4 z-50 animate-in fade-in duration-150">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] flex flex-col overflow-hidden border border-slate-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <h3 className="text-lg font-bold text-slate-900 truncate">
              Quotation Preview: <span className="font-mono text-rose-600">{quotation.name}</span>
            </h3>
            <span className="bg-blue-50 text-blue-700 border border-blue-200 text-xs font-semibold px-2.5 py-0.5 rounded-full shrink-0">
              {quotation.status}
            </span>
            {pages.length > 1 && (
              <span className="text-[11px] text-slate-500 shrink-0">{pages.length} pages</span>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={handlePrint}
              disabled={isPrinting || isDownloading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors shadow-2xs disabled:opacity-50"
            >
              {isPrinting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />}
              <span>Print</span>
            </button>

            <button
              type="button"
              onClick={handleDownload}
              disabled={isDownloading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-slate-900 hover:bg-black rounded-lg transition-colors shadow-xs disabled:opacity-50"
            >
              {isDownloading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Download className="w-3.5 h-3.5" />
              )}
              <span>Download PDF</span>
            </button>

            <button
              type="button"
              onClick={onClose}
              className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors ml-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable multi-page A4 preview */}
        <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 bg-slate-100">
          <div className="mx-auto w-full max-w-[794px] space-y-6">
            {pages.map((pageItems, pageIdx) => {
              const offset = pageOffsets[pageIdx] || 0;
              const isLast = pageIdx === pages.length - 1;
              const isFirst = pageIdx === 0;

              return (
                <div
                  key={`quote-page-${pageIdx}`}
                  className="bg-white rounded-xl shadow-md border border-slate-200/80 p-8 sm:p-10 text-slate-800"
                >
                  {isFirst ? (
                    <>
                      <div className="flex justify-between items-start mb-6 pb-5 border-b border-slate-100">
                        <div className="w-1/2 min-w-0 pr-3">
                          {companyLogo && (
                            <img
                              src={companyLogo}
                              alt={companyName}
                              className="max-h-14 mb-3 object-contain"
                            />
                          )}
                          <div className="text-xl font-bold text-slate-900 mb-1">{companyName}</div>
                          {companyAddress && (
                            <div className="text-xs text-slate-500 leading-relaxed">{companyAddress}</div>
                          )}
                          {companyPhone && (
                            <div className="text-xs text-slate-500">{companyPhone}</div>
                          )}
                        </div>
                        <div className="w-1/2 text-right">
                          <div className="text-3xl font-light text-slate-900 tracking-wider mb-3">
                            QUOTATION
                          </div>
                          <div className="inline-block text-xs text-slate-600 text-left">
                            <div className="flex justify-between gap-6 py-0.5">
                              <span className="text-slate-400 font-medium">Quote ID:</span>
                              <span className="font-mono font-bold text-slate-900">
                                {quotation.name}
                              </span>
                            </div>
                            <div className="flex justify-between gap-6 py-0.5">
                              <span className="text-slate-400 font-medium">Date:</span>
                              <span className="font-semibold text-slate-800">{quotationDate}</span>
                            </div>
                            <div className="flex justify-between gap-6 py-0.5">
                              <span className="text-slate-400 font-medium">Valid Until:</span>
                              <span className="font-semibold text-slate-800">{validUntil}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mb-6 p-4 bg-slate-50/80 rounded-xl border border-slate-100">
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                          Quotation For
                        </div>
                        <div className="text-sm font-bold text-slate-900 mb-0.5">{customerName}</div>
                        {customerContact && (
                          <div className="text-xs text-slate-500 leading-relaxed">
                            {customerContact}
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between items-center mb-4 pb-3 border-b border-slate-200 text-sm">
                      <span className="font-bold text-slate-900">
                        {companyName} — Quotation continued
                      </span>
                      <span className="font-mono text-xs text-slate-500">{quotation.name}</span>
                    </div>
                  )}

                  <div className="mb-4 overflow-hidden rounded-xl border border-slate-200">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold uppercase text-[10px] tracking-wider">
                          <th className="py-3 px-4">Description</th>
                          <th className="py-3 px-4 text-center w-20">Qty</th>
                          <th className="py-3 px-4 text-right w-28">Price</th>
                          <th className="py-3 px-4 text-right w-28">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {pageItems.map((item, idx) => (
                          <tr key={`${pageIdx}-${idx}`} className="hover:bg-slate-50/50">
                            <td className="py-3 px-4">
                              <div className="font-bold text-slate-900">
                                <span className="text-slate-400 font-medium mr-1">
                                  {offset + idx + 1}.
                                </span>
                                {item.item_name}
                              </div>
                              {item.line_comment ? (
                                <div className="text-[11px] text-slate-600 italic mt-0.5 font-normal">
                                  {item.line_comment}
                                </div>
                              ) : null}
                              <div className="text-[11px] text-slate-400 font-mono mt-0.5">
                                {item.item_code}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-center font-medium text-slate-700">
                              {item.quantity}
                            </td>
                            <td className="py-3 px-4 text-right font-medium text-slate-700">
                              {formatMoney(item.rate)}
                            </td>
                            <td className="py-3 px-4 text-right font-bold text-slate-900">
                              {formatMoney(item.amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {isLast ? (
                    <div className="flex justify-end mb-6">
                      <div className="w-72 space-y-2 text-xs">
                        <div className="flex justify-between text-slate-600 py-1">
                          <span>Subtotal:</span>
                          <span className="font-semibold text-slate-800">
                            {formatMoney(quotation.financials.subtotal)}
                          </span>
                        </div>
                        {quotation.financials.vat > 0 && (
                          <div className="flex justify-between text-slate-600 py-1">
                            <span>Tax (VAT):</span>
                            <span className="font-semibold text-slate-800">
                              {formatMoney(quotation.financials.vat)}
                            </span>
                          </div>
                        )}
                        {discAmt > 0 && (
                          <div className="flex justify-between text-emerald-600 py-1">
                            <span>
                              Discount
                              {discPct != null && discPct > 0 ? ` (${discPct}%)` : ''}:
                            </span>
                            <span className="font-semibold">- {formatMoney(discAmt)}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-slate-900 font-extrabold text-base pt-2 border-t-2 border-slate-900">
                          <span>Total:</span>
                          <span>{formatMoney(quotation.financials.total)}</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-right text-[11px] text-slate-400 mb-4">
                      Continued on next page…
                    </p>
                  )}

                  <div className="pt-4 border-t border-slate-200 flex justify-between items-center text-[11px] text-slate-400">
                    <div>
                      Prepared by: <span className="font-semibold text-slate-600">{preparer}</span>
                    </div>
                    <div className="font-bold text-slate-700">
                      Page {pageIdx + 1} of {pages.length}
                    </div>
                    <div>{footerText}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
